/**
 * Logger service for application logging
 * Provides consistent logging interface across the application
 */

import type { SecurityLogEntry } from '@/types/admin'
import * as preferences from './storage/preferences'

// Re-export SecurityLogEntry for use in components
export type { SecurityLogEntry } from '@/types/admin'

export enum LogLevel {
    DEBUG = 'debug',
    INFO = 'info',
    WARN = 'warn',
    ERROR = 'error'
}

export type LogCategory =
    | 'sync'
    | 'auth'
    | 'attendance'
    | 'location'
    | 'qr'
    | 'ble'
    | 'api'
    | 'admin'
    | 'system'
    | 'security'

export type SecurityFailureType =
    | 'invalid_qr'
    | 'expired_qr'
    | 'replay_attempt'
    | 'geofence_failure'
    | 'repeated_failure'

export type SyncStatus = 'pending' | 'synced' | 'failed'

export interface LogEntry {
    level: LogLevel
    message: string
    timestamp: Date
    context?: Record<string, unknown>
    error?: Error
}

export interface AdminLogEntry extends Omit<LogEntry, 'timestamp'> {
    id: string
    timestamp: string // ISO string for storage
    category: LogCategory
    isSuspicious?: boolean
    workerId?: number
}



const ADMIN_LOGS_KEY = 'admin_logs'
const SECURITY_LOGS_KEY = 'security_logs'
const MAX_ADMIN_LOGS = 1000
const MAX_SECURITY_LOGS = 500

// Suspicious activity thresholds
const SUSPICIOUS_THRESHOLDS = {
    QR_VALIDATION_FAILURES: 3, // 3 failures in 5 minutes
    LOCATION_VALIDATION_FAILURES: 5, // 5 failures in 5 minutes
    AUTH_FAILURES: 3, // Already handled by admin store
    TIME_WINDOW_MS: 5 * 60 * 1000 // 5 minutes
}

// Security log thresholds
const SECURITY_THRESHOLDS = {
    REPEATED_FAILURE_COUNT: 3, // 3 failures triggers alert
    REPEATED_FAILURE_WINDOW_MS: 60 * 60 * 1000 // 1 hour
}

/**
 * Logger service for application-wide logging
 */
export class LoggerService {
    private static instance: LoggerService
    private logs: LogEntry[] = []
    private adminLogs: AdminLogEntry[] = []
    private securityLogs: SecurityLogEntry[] = []
    private maxLogs = 1000
    private isInitialized = false

    private constructor() {}

    static getInstance(): LoggerService {
        if (!LoggerService.instance) {
            LoggerService.instance = new LoggerService()
        }
        return LoggerService.instance
    }

    /**
     * Initialize logger service and load persisted admin logs and security logs
     */
    async initialize(): Promise<void> {
        if (this.isInitialized) return

        try {
            const storedLogs = await preferences.getObject<AdminLogEntry[]>(ADMIN_LOGS_KEY)
            if (storedLogs && Array.isArray(storedLogs)) {
                this.adminLogs = storedLogs
            }

            const storedSecurityLogs =
                await preferences.getObject<SecurityLogEntry[]>(SECURITY_LOGS_KEY)
            if (storedSecurityLogs && Array.isArray(storedSecurityLogs)) {
                this.securityLogs = storedSecurityLogs
            }

            this.isInitialized = true
        } catch (error) {
            console.error('Failed to load admin logs from storage', error)
            // Don't wipe existing in-memory logs on storage read failure
            // Mark as initialized so the app doesn't hang waiting
            this.isInitialized = true
        }
    }

    /**
     * Log a debug message
     */
    debug(message: string, context?: Record<string, unknown>): void {
        this.log(LogLevel.DEBUG, message, context)
    }

    /**
     * Log an info message
     */
    info(message: string, context?: Record<string, unknown>): void {
        this.log(LogLevel.INFO, message, context)
    }

    /**
     * Log a warning message
     */
    warn(message: string, context?: Record<string, unknown>): void {
        this.log(LogLevel.WARN, message, context)
    }

    /**
     * Log an error message
     */
    error(message: string, error?: Error, context?: Record<string, unknown>): void {
        this.log(LogLevel.ERROR, message, context, error)
    }

    /**
     * Internal logging method
     */
    private log(
        level: LogLevel,
        message: string,
        context?: Record<string, unknown>,
        error?: Error
    ): void {
        const entry: LogEntry = {
            level,
            message,
            timestamp: new Date(),
            context,
            error
        }

        // Add to internal logs
        this.logs.push(entry)
        if (this.logs.length > this.maxLogs) {
            this.logs.shift()
        }

        // Console logging based on level
        const logMessage = `[${level.toUpperCase()}] ${message}`
        const logContext = context ? { ...context, error } : { error }

        switch (level) {
            case LogLevel.DEBUG:
                console.debug(logMessage, logContext)
                break
            case LogLevel.INFO:
                console.info(logMessage, logContext)
                break
            case LogLevel.WARN:
                console.warn(logMessage, logContext)
                break
            case LogLevel.ERROR:
                console.error(logMessage, logContext)
                break
        }
    }

    /**
     * Get all logs
     */
    getLogs(): LogEntry[] {
        return [...this.logs]
    }

    /**
     * Get logs by level
     */
    getLogsByLevel(level: LogLevel): LogEntry[] {
        return this.logs.filter(log => log.level === level)
    }

    /**
     * Clear all logs
     */
    clearLogs(): void {
        this.logs = []
    }

    /**
     * Export logs as JSON
     */
    exportLogs(): string {
        return JSON.stringify(this.logs, null, 2)
    }

    /**
     * Log an admin-level event with category and suspicious activity detection
     */
    async logAdmin(
        level: LogLevel,
        category: LogCategory,
        message: string,
        context?: Record<string, unknown>,
        error?: Error,
        workerId?: number
    ): Promise<void> {
        // Ensure initialized
        if (!this.isInitialized) {
            await this.initialize()
        }

        // Create admin log entry
        const logEntry: AdminLogEntry = {
            id: crypto.randomUUID(),
            level,
            category,
            message,
            timestamp: new Date().toISOString(),
            context,
            error: error
                ? ({ name: error.name, message: error.message, stack: error.stack } as Error)
                : undefined,
            workerId,
            isSuspicious: false
        }

        // Detect suspicious activity
        logEntry.isSuspicious = this.detectSuspiciousActivity(category, level, message)

        // Add to admin logs
        this.adminLogs.push(logEntry)

        // Apply retention policy (max entries)
        if (this.adminLogs.length > MAX_ADMIN_LOGS) {
            const permanentLogs = this.adminLogs.filter(
                log => (log as SecurityLogEntry).isPermanent === true
            )
            const regularLogs = this.adminLogs.filter(log => !(log as SecurityLogEntry).isPermanent)

            // Cap permanent logs in admin view too
            const cappedPermanentLogs = permanentLogs.slice(-MAX_SECURITY_LOGS)
            const regularSlots = Math.max(0, MAX_ADMIN_LOGS - cappedPermanentLogs.length)
            const prunedRegularLogs = regularLogs.slice(-regularSlots)
            this.adminLogs = [...cappedPermanentLogs, ...prunedRegularLogs]
        }

        // Persist to storage
        await this.persistAdminLogs()

        // Also log to console
        this.log(level, `[${category}] ${message}`, context, error)
    }

    /**
     * Log a security event with detailed context
     */
    async logSecurity(
        level: LogLevel,
        category: LogCategory,
        message: string,
        failureType: SecurityFailureType,
        context?: {
            workerId?: number
            location?: { lat: number; lng: number }
            qrPayload?: { timestamp: string; nonce?: string }
            failureReason: string
            attemptCount?: number
        }
    ): Promise<void> {
        // Ensure initialized
        if (!this.isInitialized) {
            await this.initialize()
        }

        // Create security log entry
        const logEntry: SecurityLogEntry = {
            id: crypto.randomUUID(),
            level,
            category,
            message,
            timestamp: new Date().toISOString(),
            workerId: context?.workerId,
            isSuspicious: true,
            isPermanent: true, // Never auto-delete
            syncStatus: 'pending',
            syncAttempts: 0,
            failureType,
            context
        }

        // Add to security logs
        this.securityLogs.push(logEntry)

        // Auto-prune security logs if over limit (keep newest)
        if (this.securityLogs.length > MAX_SECURITY_LOGS) {
            this.securityLogs = this.securityLogs.slice(-MAX_SECURITY_LOGS)
        }

        // Also add to admin logs for unified view (marked as permanent)
        this.adminLogs.push(logEntry)

        // Check for repeated failures and generate alert if needed
        await this.checkRepeatedFailures(context?.workerId)

        // Persist to storage
        await this.persistSecurityLogs()
        await this.persistAdminLogs()

        // Also log to console
        this.log(
            LogLevel.ERROR,
            `[SECURITY] [${failureType}] ${message}`,
            context as Record<string, unknown>
        )
    }

    /**
     * Check for repeated failures and generate alert if threshold exceeded
     */
    private async checkRepeatedFailures(
        workerId?: number
    ): Promise<void> {
        if (!workerId) return

        const now = Date.now()
        const timeWindowStart = now - SECURITY_THRESHOLDS.REPEATED_FAILURE_WINDOW_MS

        // Count recent failures for this worker (exclude repeated_failure alerts themselves)
        const recentFailures = this.securityLogs.filter(
            log =>
                log.workerId === workerId &&
                log.failureType !== 'repeated_failure' &&
                new Date(log.timestamp).getTime() >= timeWindowStart
        )

        // If threshold exceeded, create alert log
        if (recentFailures.length >= SECURITY_THRESHOLDS.REPEATED_FAILURE_COUNT) {
            const alertEntry: SecurityLogEntry = {
                id: crypto.randomUUID(),
                level: LogLevel.ERROR,
                category: 'security',
                message: `ALERT: Worker ${workerId} has ${recentFailures.length} failed check-in attempts in 1 hour`,
                timestamp: new Date().toISOString(),
                workerId,
                isSuspicious: true,
                isPermanent: true,
                syncStatus: 'pending',
                syncAttempts: 0,
                failureType: 'repeated_failure',
                context: {
                    failureReason: `${recentFailures.length} failures in 1 hour window`,
                    attemptCount: recentFailures.length
                }
            }

            // Add to both security and admin logs
            this.securityLogs.push(alertEntry)
            this.adminLogs.push(alertEntry)

            // Persist
            await this.persistSecurityLogs()
            await this.persistAdminLogs()

            // Log to console
            this.log(LogLevel.ERROR, `[SECURITY] [ALERT] ${alertEntry.message}`, {
                failureCount: recentFailures.length
            })
        }
    }

    /**
     * Persist security logs to storage
     */
    private async persistSecurityLogs(): Promise<void> {
        try {
            await preferences.setObject(SECURITY_LOGS_KEY, this.securityLogs)
        } catch (error) {
            console.error('Failed to persist security logs', error)
        }
    }

    /**
     * Get all security logs
     */
    getSecurityLogs(filterType?: SecurityFailureType): SecurityLogEntry[] {
        if (filterType) {
            return this.securityLogs.filter(log => log.failureType === filterType).reverse()
        }
        return [...this.securityLogs].reverse() // Newest first
    }

    /**
     * Get security logs by failure type
     */
    getSecurityLogsByType(failureType: SecurityFailureType): SecurityLogEntry[] {
        return this.securityLogs.filter(log => log.failureType === failureType).reverse()
    }

    /**
     * Get security logs by worker ID
     */
    getSecurityLogsByWorker(workerId: number): SecurityLogEntry[] {
        return this.securityLogs.filter(log => log.workerId === workerId).reverse()
    }

    /**
     * Get pending security logs for sync
     */
    getPendingSecurityLogs(): SecurityLogEntry[] {
        return this.securityLogs.filter(log => log.syncStatus === 'pending')
    }

    /**
     * Update security log sync status
     */
    async updateSecurityLogSyncStatus(logId: string, status: SyncStatus): Promise<void> {
        const log = this.securityLogs.find(l => l.id === logId)
        if (log) {
            log.syncStatus = status
            if (status === 'failed') {
                log.syncAttempts++
            }
            await this.persistSecurityLogs()
        }
    }

    /**
     * Export security logs as formatted text
     */
    exportSecurityLogs(): string {
        try {
            const logs = this.getSecurityLogs()

            if (logs.length === 0) {
                return 'No security logs found'
            }

            const lines = ['Security Logs Export', '='.repeat(50), '']

            logs.forEach(log => {
                const timestamp = new Date(log.timestamp).toLocaleString()
                const workerId = log.workerId ? ` (Worker: ${log.workerId})` : ''
                const syncStatus = ` [Sync: ${log.syncStatus}]`

                lines.push(
                    `[${timestamp}] ${log.level.toUpperCase()} [${log.failureType}]${workerId}${syncStatus}`
                )
                lines.push(`  ${log.message}`)

                if (log.context) {
                    lines.push(`  Failure Reason: ${log.context.failureReason}`)

                    if (log.context.location) {
                        lines.push(
                            `  Location: ${log.context.location.lat}, ${log.context.location.lng}`
                        )
                    }

                    if (log.context.qrPayload) {
                        lines.push(`  QR Timestamp: ${log.context.qrPayload.timestamp}`)
                    }

                    if (log.context.attemptCount) {
                        lines.push(`  Attempt Count: ${log.context.attemptCount}`)
                    }
                }

                if (log.context) {
                    lines.push(`  Additional Context: ${JSON.stringify(log.context)}`)
                }

                lines.push('') // Empty line between entries
            })

            return lines.join('\n')
        } catch (error) {
            return 'Error exporting security logs'
        }
    }

    /**
     * Clear security logs (requires explicit confirmation)
     */
    async clearSecurityLogs(): Promise<void> {
        // Remove security logs from both stores
        this.securityLogs = []
        this.adminLogs = this.adminLogs.filter(log => !(log as SecurityLogEntry).isPermanent)

        await preferences.remove(SECURITY_LOGS_KEY)
        await this.persistAdminLogs()
    }

    /**
     * Cleanup logs - remove old logs but preserve security logs
     */
    async cleanupLogs(): Promise<void> {
        // Prune security logs if over limit
        if (this.securityLogs.length > MAX_SECURITY_LOGS) {
            this.securityLogs = this.securityLogs.slice(-MAX_SECURITY_LOGS)
            await this.persistSecurityLogs()
        }

        // Remove non-security admin logs if over limit
        if (this.adminLogs.length > MAX_ADMIN_LOGS) {
            const securityLogs = this.adminLogs.filter(log => (log as SecurityLogEntry).isPermanent)
            const nonSecurityLogs = this.adminLogs.filter(
                log => !(log as SecurityLogEntry).isPermanent
            )

            const cappedSecurityLogs = securityLogs.slice(-MAX_SECURITY_LOGS)
            const regularSlots = Math.max(0, MAX_ADMIN_LOGS - cappedSecurityLogs.length)
            const recentNonSecurityLogs = nonSecurityLogs
                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                .slice(0, regularSlots)

            this.adminLogs = [...cappedSecurityLogs, ...recentNonSecurityLogs]
            await this.persistAdminLogs()
        }
    }

    /**
     * Detect suspicious activity patterns
     */
    private detectSuspiciousActivity(
        category: LogCategory,
        level: LogLevel,
        message: string
    ): boolean {
        const now = Date.now()
        const timeWindowStart = now - SUSPICIOUS_THRESHOLDS.TIME_WINDOW_MS

        // Check for QR validation failures
        if (category === 'qr' && (level === LogLevel.WARN || level === LogLevel.ERROR)) {
            const recentQrFailures = this.adminLogs.filter(
                log =>
                    log.category === 'qr' &&
                    (log.level === LogLevel.WARN || log.level === LogLevel.ERROR) &&
                    new Date(log.timestamp).getTime() >= timeWindowStart
            ).length

            if (recentQrFailures >= SUSPICIOUS_THRESHOLDS.QR_VALIDATION_FAILURES - 1) {
                return true
            }
        }

        // Check for location validation failures
        if (category === 'location' && (level === LogLevel.WARN || level === LogLevel.ERROR)) {
            const recentLocationFailures = this.adminLogs.filter(
                log =>
                    log.category === 'location' &&
                    (log.level === LogLevel.WARN || log.level === LogLevel.ERROR) &&
                    new Date(log.timestamp).getTime() >= timeWindowStart
            ).length

            if (recentLocationFailures >= SUSPICIOUS_THRESHOLDS.LOCATION_VALIDATION_FAILURES - 1) {
                return true
            }
        }

        // Check message content for suspicious keywords
        const suspiciousKeywords = ['spoof', 'mock', 'fake', 'bypass', 'tamper', 'unauthorized']
        const messageLower = message.toLowerCase()
        if (suspiciousKeywords.some(keyword => messageLower.includes(keyword))) {
            return true
        }

        return false
    }

    /**
     * Persist admin logs to storage
     */
    private async persistAdminLogs(): Promise<void> {
        try {
            await preferences.setObject(ADMIN_LOGS_KEY, this.adminLogs)
        } catch (error) {
            console.error('Failed to persist admin logs', error)
        }
    }

    /**
     * Get all admin logs
     */
    getAdminLogs(): AdminLogEntry[] {
        return [...this.adminLogs].reverse() // Newest first
    }

    /**
     * Get admin logs by category
     */
    getAdminLogsByCategory(category: LogCategory): AdminLogEntry[] {
        return this.adminLogs.filter(log => log.category === category).reverse()
    }

    /**
     * Get admin logs by level
     */
    getAdminLogsByLevel(level: LogLevel): AdminLogEntry[] {
        return this.adminLogs.filter(log => log.level === level).reverse()
    }

    /**
     * Get suspicious admin logs
     */
    getSuspiciousLogs(): AdminLogEntry[] {
        return this.adminLogs.filter(log => log.isSuspicious).reverse()
    }

    /**
     * Search admin logs
     */
    searchAdminLogs(query: string): AdminLogEntry[] {
        const lowerQuery = query.toLowerCase()
        return this.adminLogs
            .filter(
                log =>
                    log.message.toLowerCase().includes(lowerQuery) ||
                    log.category.toLowerCase().includes(lowerQuery) ||
                    (log.workerId !== undefined &&
                        String(log.workerId).toLowerCase().includes(lowerQuery))
            )
            .reverse()
    }

    /**
     * Clear admin logs
     */
    async clearAdminLogs(): Promise<void> {
        this.adminLogs = []
        await preferences.remove(ADMIN_LOGS_KEY)
    }

    /**
     * Export admin logs as formatted text
     */
    exportAdminLogs(): string {
        const lines = ['WARDEN ACTIVITY LOGS', '='.repeat(80), '']

        this.getAdminLogs().forEach(log => {
            const timestamp = new Date(log.timestamp).toLocaleString()
            const suspicious = log.isSuspicious ? ' [SUSPICIOUS]' : ''
            const workerId = log.workerId ? ` (Worker: ${log.workerId})` : ''

            lines.push(
                `[${timestamp}] ${log.level.toUpperCase()} [${log.category}]${suspicious}${workerId}`
            )
            lines.push(`  ${log.message}`)

            if (log.context) {
                lines.push(`  Context: ${JSON.stringify(log.context)}`)
            }

            if (log.error) {
                lines.push(`  Error: ${log.error.message}`)
            }

            lines.push('') // Empty line between entries
        })

        return lines.join('\n')
    }

    /**
     * Get admin logs count
     */
    getAdminLogsCount(): number {
        return this.adminLogs.length
    }

    /**
     * Get suspicious logs count
     */
    getSuspiciousLogsCount(): number {
        return this.adminLogs.filter(log => log.isSuspicious).length
    }
}

// Export singleton instance
export const logger = LoggerService.getInstance()
